import { useCallback, useMemo, useState } from 'react'
import { InventoryItem, Venue, InventoryType, VenueEvent } from 'types/model'

import {
  useVenue,
  useVenueDaySettings,
  useVenueLayouts,
  useVenueSections,
  useVenueTables,
} from '@modules/venue/data'
import { useVenueDayEvent } from '@modules/event/data'
import { useInventoryList } from '@modules/inventory/data'
import { InventoryListView } from '@modules/inventory/views/inventory-list-view'
import { InventoryDetailModal } from '../views/inventory-detail-modal'

import { getBusinessDate, getOffsetUTC, isoToUTC, utcToISO } from '@utils/date-time'
import { formatPrice, getId } from '@utils/strings'
import { useCheckoutContext } from '@hooks/checkout'
import { VenueLayout, VenueLayoutSection, VenueLayoutTable } from 'types/model/venue-layout'
import { translateInventorySort } from '../utils'
import { useAttribution } from '@hooks/attribution'

export interface InventoryListProps {
  venue: Venue
  event?: VenueEvent
  initialDate?: string
  tickets?: InventoryItem[]
  ticketCount?: number
  packages?: InventoryItem[]
  packageCount?: number
  showHeader?: boolean
  stickyHeader?: boolean
  showDatePicker?: boolean
  showEvent?: boolean
  showEventPoster?: boolean
  stickyMap?: boolean
  hideMap?: boolean
  layouts?: VenueLayout[]
  sections?: VenueLayoutSection[]
  tables?: VenueLayoutTable[]
}

export function InventoryList({
  venue: _venue,
  event: _event,
  tickets: _tickets,
  packages: _packages,
  layouts: _layouts = [],
  sections: _sections = [],
  tables: _tables = [],
  initialDate,
  showEvent,
  showHeader = true,
  stickyHeader = true,
  showDatePicker = true,
  showEventPoster = false,
  stickyMap = false,
  hideMap = false,
}: InventoryListProps) {
  const venueId = getId(_venue)
  const checkoutCtx = useCheckoutContext()
  const { affiliateId, attributionId } = useAttribution()

  const {
    venue,
    event,
    daySettings,
    tickets,
    packages,
    layouts,
    sections,
    tables,
    date,
    minDate,
    maxDate,
    handleDateChange,
  } = useInventoryListController({
    venueId,
    initialDate,
    fallbackData: {
      venue: _venue,
      event: _event,
      tickets: _tickets,
      packages: _packages,
      layouts: _layouts,
      sections: _sections,
      tables: _tables,
    },
  })

  const showMap =
    !hideMap &&
    venue &&
    (event.data?.isMapWidgetEnabled ?? venue.data?.eventDefault.isMapWidgetEnabled)

  return (
    <div className="w-full @container bg-inherit">
      <InventoryListView
        date={date}
        daySettings={daySettings}
        attributionId={attributionId}
        affiliateId={affiliateId}
        minDate={minDate}
        maxDate={maxDate}
        venue={venue.data as Venue}
        event={event.data}
        onDateChange={showDatePicker ? handleDateChange : undefined}
        packages={packages.data}
        packagesLoading={packages.loading || !packages.data}
        tickets={tickets.data}
        ticketsLoading={tickets.loading || !tickets.data}
        showHeader={showHeader}
        stickyHeader={stickyHeader}
        showEventHeader={showEvent}
        showEventPoster={showEventPoster}
        onInventoryAdd={checkoutCtx.selectInventory}
        stickyMap={stickyMap}
        showMap={showMap}
        layouts={layouts}
        sections={sections}
        tables={tables}
      />
      <InventoryDetailModal
        {...checkoutCtx.inventoryModal}
        venue={venue.data}
        event={event.data}
        inventory={checkoutCtx.selectedInventory}
        onCheckout={checkoutCtx.beginCheckout}
      />
    </div>
  )
}

export function useInventoryListController({
  venueId,
  initialDate,
  fallbackData,
}: {
  venueId: string
  initialDate?: string
  fallbackData?: {
    venue: Venue
    event?: VenueEvent
    tickets?: InventoryItem[]
    packages?: InventoryItem[]
    layouts?: VenueLayout[]
    sections?: VenueLayoutSection[]
    tables?: VenueLayoutTable[]
  }
}) {
  const { venue, event, tickets, packages, layouts, sections, tables } = fallbackData ?? {}

  const [date, setDate] = useState<string | undefined>(
    initialDate || event
      ? utcToISO(event?.date ?? initialDate, { startOfDay: true })
      : getBusinessDate().toISOString()
  )
  const handleDateChange = useCallback((val?: number | string | null) => {
    const dt = val ? new Date(val).toISOString() : undefined
    setDate(dt)
  }, [])

  const dateUTC = useMemo(() => getOffsetUTC(date), [date])

  const inventoryParams = useMemo(
    () => ({
      dateStart: isoToUTC(date)?.toString(),
      dateEnd: isoToUTC(date)?.toString(),
      sort: translateInventorySort(venue?.inventorySortOrder),
      limit: 500,
      skip: 0,
    }),
    [date, venue?.inventorySortOrder]
  )

  const ticketParams = useMemo(
    () => ({
      ...inventoryParams,
      type: InventoryType.Ticket,
    }),
    [inventoryParams]
  )
  const packageParams = useMemo(
    () => ({
      ...inventoryParams,
      type: InventoryType.Package,
    }),
    [inventoryParams]
  )

  const currentVenue = useVenue(venueId, {
    config: { fallbackData: venue },
  })

  const venueDaySettings = useVenueDaySettings(venueId, dateUTC)

  const [minDate, maxDate] = useMemo(() => {
    const now = new Date()
    const nowYear = now.getFullYear()
    const nowMonth = now.getMonth()
    const nowDay = now.getDate()
    const start = getBusinessDate()
    const purchaseWindow = currentVenue.data?.inventoryTimeframe ?? 30
    const end = new Date(nowYear, nowMonth, nowDay + purchaseWindow, 0, 0, 0)
    return [start.valueOf(), end.valueOf()]
  }, [currentVenue.data?.inventoryTimeframe])

  const currentEvent = useVenueDayEvent(venueId, date, {
    pause: !!event,
    config: { fallbackData: event ? [event] : undefined },
    // pause: !showEvent
  })
  const currentTickets = useInventoryList(venueId, {
    params: ticketParams,
    config: { fallbackData: tickets },
  })
  const currentPackages = useInventoryList(venueId, {
    params: packageParams,
    config: { fallbackData: packages },
  })

  const currentLayouts = useVenueLayouts(venue ? getId(venue) : null, {
    config: { fallbackData: layouts },
  })
  const currentSections = useVenueSections(venue ? getId(venue) : null, {
    config: { fallbackData: sections },
  })
  const currentTables = useVenueTables(venue ? getId(venue) : null, {
    config: { fallbackData: tables },
  })

  const tierFlattenedTickets = useMemo(() => {
    let allTickets: InventoryItem[] = []
    if (!currentTickets.data) return allTickets
    for (let i = 0; i < currentTickets.data.length; ++i) {
      const inv = currentTickets.data[i]
      if (inv.hasTiers <= 1) {
        allTickets.push(inv)
        continue
      } else {
        let tierIndex = 0
        let activeTier = inv.tier.activeLevel
        const activeTierIndex = inv.tier.levels.findIndex((tier) => tier.id === activeTier)

        while (tierIndex <= activeTierIndex) {
          const invDeepCopy = { ...inv }
          const currentTier = inv.tier.levels[tierIndex]
          invDeepCopy.price = currentTier.price
          invDeepCopy.name = `${invDeepCopy.name} - (${formatPrice(currentTier.price, {
            currency: venue?.currencyCode,
            maximumFractionDigits: 0,
          })})`
          if (tierIndex !== activeTierIndex) {
            invDeepCopy.quantity = 0
            invDeepCopy.quantityLeft = 0
            invDeepCopy.id = `${invDeepCopy.id}-${currentTier.id}`
          }
          allTickets.push(invDeepCopy)
          ++tierIndex
        }
      }
    }
    return allTickets
  }, [currentTickets.data, venue?.currencyCode])

  return {
    venue: currentVenue,
    //@ts-ignore
    event: { data: event } ?? currentEvent,
    tickets: { ...currentTickets, data: tierFlattenedTickets },
    packages: currentPackages,
    layouts: currentLayouts.data,
    sections: currentSections.data,
    tables: currentTables.data,
    date,
    daySettings: venueDaySettings.data,
    minDate,
    maxDate,
    handleDateChange,
  }
}
